import { STRAPI_IMGX, isActiveLocale } from '../../../constants'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import { normaliseLink } from '../../../controllers/helpers/normaliseLink'
import Image from 'next/image'
import { externalImageLoader } from '../../../utilities/externalImageLoader'
import { listicleMarkdownComponents } from './Listicle'
import { FilledStarIcon, HalfFilledStarIcon, OutlineStarIcon } from '../../Images'
import { VeeVenue } from '../../../models/types/VeeVenue.type'

type PostProps = {
  venue: VeeVenue
  postIndex: number
  callToActionURL: string
  locale: string
  isBlogArticle: boolean
}

const calculateStars = (googleRating: number) => {
  const roundedToNearest = Math.round(googleRating * 2) / 2

  const roundedRating = Math.floor(roundedToNearest)
  const displayHalfStar = roundedToNearest % 1 !== 0
  const remainingStars = displayHalfStar ? 5 - roundedRating - 1 : 5 - roundedRating

  return { filledStars: roundedRating, emptyStars: remainingStars, displayHalfStar }
}

const Post = ({ venue, postIndex, callToActionURL, locale, isBlogArticle }: PostProps) => {
  const ASPECT_RATIO = 1.458
  const HEIGHT = 480
  const IMG = { width: HEIGHT * ASPECT_RATIO, height: HEIGHT }
  const LISTICLE_SETTINGS = `?auto=format&sharp=5&con=15&fit=crop&w=${IMG.width * 2}&h=${IMG.height * 2}`

  const getImageSrc = (venue: VeeVenue, num: number) => {
    return `${STRAPI_IMGX}/${venue.cover[num]?.hash + venue.cover[num]?.ext}${LISTICLE_SETTINGS}`
  }

  const isLandingPageLocaleActive = isActiveLocale(locale)

  const introduction = venue.introduction.split(/\n(?!(\s*-|\s*\d+\.))/g).filter((e) => e)

  const getFormattedPostTitle = (index: number, postName: string) => {
    return !!isBlogArticle ? postName : `${index + 1}. ${postName}`
  }

  const buildStarComponent = ({
    filledStars,
    emptyStars,
    displayHalfStar
  }: {
    filledStars: number
    emptyStars: number
    displayHalfStar: boolean
  }) => {
    return (
      <>
        {Array.from(Array(filledStars).keys()).map((_, i) => {
          return <FilledStarIcon key={i} style={{ width: 20, height: 20, alignSelf: 'center' }} />
        })}
        {displayHalfStar && <HalfFilledStarIcon style={{ width: 20, height: 20, alignSelf: 'center' }} />}
        {Array.from(Array(emptyStars).keys()).map((_, i) => {
          return <OutlineStarIcon key={i} style={{ width: 20, height: 20, alignSelf: 'center' }} />
        })}
      </>
    )
  }

  const reviewsEnabled = venue.google_reviews_enabled && venue.google_rating && venue.google_total_ratings

  const { filledStars, emptyStars, displayHalfStar } = calculateStars(venue.google_rating)

  return (
    <div className='container'>
      <section className='post-images'>
        <div className='img-hero'>
          <Image
            src={getImageSrc(venue, 0)}
            alt={
              venue.cover[0]?.alternativeText !== '' ? venue.cover[0]?.alternativeText : `${venue.name}${1}`
            }
            loader={externalImageLoader}
            unoptimized
            fill
            style={{ objectFit: 'cover' }}
            sizes='(max-width: 1200px) 330px,
              700px'
          />
        </div>
        {venue.cover.length === 3 && (
          <div className='secondary-images'>
            <div className='img-top-right'>
              <Image
                src={getImageSrc(venue, 1)}
                alt={
                  venue.cover[1]?.alternativeText !== ''
                    ? venue.cover[1]?.alternativeText
                    : `${venue.name} image ${2}`
                }
                loader={externalImageLoader}
                unoptimized
                fill
                style={{ objectFit: 'cover' }}
                sizes='(max-width: 1200px) 330px,
              700px'
              />
            </div>
            <div className='img-top-left'>
              <Image
                src={getImageSrc(venue, 2)}
                alt={
                  venue.cover[2]?.alternativeText !== ''
                    ? venue.cover[2]?.alternativeText
                    : `${venue.name} image ${3}`
                }
                loader={externalImageLoader}
                unoptimized
                aria-hidden={true}
                fill
                style={{ objectFit: 'cover' }}
                sizes='(max-width: 1200px) 330px,
              700px'
              />
            </div>
          </div>
        )}
      </section>
      <section className={isBlogArticle ? 'post-content blog-article' : 'post-content'}>
        <h3 className='post-title'>{getFormattedPostTitle(postIndex, venue.name)}</h3>
        <div className='subtitle-container'>
          <p className={`post-location ${reviewsEnabled ? 'align-text' : ''}`}>{venue.location}</p>
          {reviewsEnabled && (
            <>
              <div className='subtitle-divider'></div>
              <div className='reviews-container'>
                <div className='ratings-container'>
                  {buildStarComponent({ filledStars, emptyStars, displayHalfStar })}
                  <span className='rating-text'>{venue.google_rating.toFixed(1)}</span>
                </div>
                <div>
                  <span className='reviews-text'>
                    {venue.google_total_ratings} Google{' '}
                    {venue.google_total_ratings > 1 ? 'reviews' : 'review'}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
        <div className='post-text'>
          {introduction.map((string, index) => (
            <span key={`post-section-${index}`}>
              <ReactMarkdown components={listicleMarkdownComponents} rehypePlugins={[rehypeRaw]} key={string}>
                {string}
              </ReactMarkdown>
              <br />
            </span>
          ))}
        </div>
        {(!!venue.link || isLandingPageLocaleActive) && (
          <>
            <br />
            <div className='buttons'>
              {venue.link && (
                <a
                  className='alternate-button listicle'
                  href={normaliseLink(venue.link)}
                  target={!isLandingPageLocaleActive ? '_blank' : undefined}>
                  Tell me more
                </a>
              )}
              {isLandingPageLocaleActive && !isBlogArticle && (
                <a className='button listicle' href={callToActionURL}>
                  Start booking
                </a>
              )}
            </div>
          </>
        )}
      </section>
      <style jsx>{`
        .container {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          width: 100%;
          max-width: 1200px;
          margin: 20px auto;
        }

        .post-images {
          height: clamp(350px, 54vw, 450px);
          width: 100%;
          display: flex;
          flex-flow: row;
          justify-content: center;
          gap: 8px;
        }

        .img-hero {
          height: 100%;
          max-width: 800px;
          flex: 2;
        }

        .secondary-images {
          flex: 1;
          height: 100%;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .img-top-left,
        .img-top-right {
          flex: 1;
        }

        .img-hero,
        .img-top-left,
        .img-top-right {
          position: relative;
          border-radius: 8px;
          overflow: hidden;
        }

        .post-content {
          max-width: 800px;
          margin: 40px 0 60px;
        }

        .post-content.blog-article {
          margin: 40px 0 10px;
        }

        .post-title {
          margin: 6px 6px 18px;
          font-size: 26px;
          font-weight: 600px;
        }

        .post-location {
          font-size: 18px;
          flex: 1;
        }

        .post-location.align-text {
          text-align: end;
        }

        .subtitle-container {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          margin-bottom: 18px;
        }

        @media only screen and (max-width: 375px) {
          .post-title {
            margin: 6px;
          }

          .subtitle-container {
            display: block;
          }

          .post-location {
            margin-bottom: 6px;
          }

          .post-location.align-text {
            text-align: center;
          }

          .subtitle-divider {
            display: none;
          }

          .reviews-container {
            justify-content: center;
          }
        }

        .subtitle-divider {
          width: 1px;
          background: #d9d9d9;
          padding: 12px 0;
        }

        .reviews-container {
          flex: 1;
          display: flex;
          gap: 8px;
          align-items: baseline;
        }

        .ratings-container {
          display: flex;
          align-items: baseline;
        }

        .rating-text {
          font-size: 18px;
          margin-inline-start: 4px;
        }

        .reviews-text {
          font-size: 12px;
        }

        .post-text {
          text-align: left;
          line-height: 1.7;
        }

        .buttons {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          justify-content: center;
          gap: 16px;
        }

        @media screen and (max-width: 600px) {
          .post-images {
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            gap: 12px;
          }

          .secondary-images {
            flex-direction: row;
            width: 100%;
            height: 40%;
          }

          .post-text {
            text-align: left;
            line-height: 1.1;
          }
        }
      `}</style>
    </div>
  )
}

export default Post
